import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

// Each entry is a lightweight RegisteredComponent with a dynamic() wrapper as its component.
// Creating dynamic() wrappers is near-zero cost — just lazy React component references.
// Actual module loading happens asynchronously when React renders each component:
//   - Server: next/dynamic loads synchronously → SSR preserved
//   - Client: import() loads async in separate chunks → no main thread blocking
const registry = new Map<string, RegisteredComponent>([
  // layout
  [
    "Header",
    {
      name: "Header",
      component: dynamic(() => import("@/components/Header/Header").then((m) => m.Header)),
    },
  ],
  [
    "Footer",
    {
      name: "Footer",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      component: dynamic(() => import("@/components/Footer/Footer").then((m) => m.Footer)),
    },
  ],

  // footer
  [
    "Footer ContactDetails",
    {
      name: "Footer ContactDetails",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@jti/ui/eva-components/Footer/components/ContactDetails").then((m) => m.ContactDetails)
      ),
    },
  ],
  [
    "Footer FindStoreBanner",
    {
      name: "Footer FindStoreBanner",
      component: dynamic(() =>
        import("@/components/Footer/components/FindStoreBanner/FindStoreBanner").then((m) => m.FindStoreBanner)
      ),
    },
  ],
  [
    "Footer SubscribeForm",
    {
      name: "Footer SubscribeForm",
      component: dynamic(() =>
        import("@jti/ui/eva-components/Footer/components/SubscribeForm").then((m) => m.SubscribeForm)
      ),
    },
  ],

  // soft age verification
  [
    "Soft Age Verification BirthDateForm Inputs",
    {
      name: "Soft Age Verification BirthDateForm Inputs",
      component: dynamic(() =>
        import("@/components/SoftAgeVerification/components/BirthDateFormInputs/BirthDateFormInputs").then(
          (m) => m.BirthDateFormInputs
        )
      ),
    },
  ],
  [
    "Soft Age Verification Disclaimer",
    {
      name: "Soft Age Verification Disclaimer",
      component: dynamic(() =>
        import("@/components/SoftAgeVerification/components/Disclaimer/Disclaimer").then((m) => m.Disclaimer)
      ),
    },
  ],
  [
    "Soft Age Verification Buttons",
    {
      name: "Soft Age Verification Buttons",
      noWrap: true,
      component: dynamic(() =>
        import("@/components/SoftAgeVerification/components/BirthDateCtas/BirthDateFormCtas").then(
          (m) => m.BirthDateFormCtas
        )
      ),
    },
  ],

  // live chat
  [
    "LiveChat",
    {
      name: "LiveChat",
      component: dynamic(() => import("@/components/LiveChat/LiveChat").then((m) => m.LiveChat), {
        ssr: false,
      }),
    },
  ],

  // product
  [
    "ConsumablesProductDetailsContainer",
    {
      name: "ConsumablesProductDetailsContainer",
      component: dynamic(() =>
        import("@/components/ConsumablesProductDetailsContainer/ConsumablesProductDetailsContainer").then(
          (m) => m.ConsumablesProductDetailsContainer
        )
      ),
    },
  ],
  [
    "SticksProductDetailsContainer",
    {
      name: "SticksProductDetailsContainer",
      component: dynamic(() =>
        import("@/components/SticksProductDetailsContainer/SticksProductDetailsContainer").then(
          (m) => m.SticksProductDetailsContainer
        )
      ),
    },
  ],
  [
    "ProductSpecification",
    {
      name: "ProductSpecification",
      component: dynamic(
        () => import("@/components/ProductSpecification/ProductSpecification").then((m) => m.ProductSpecification),
        { ssr: false }
      ),
    },
  ],
  [
    "ProductTeaserCarousel",
    {
      name: "ProductTeaserCarousel",
      component: dynamic(
        () => import("@/components/ProductTeaserCarousel/ProductTeaserCarousel").then((m) => m.ProductTeaserCarousel),
        { ssr: false }
      ),
    },
  ],
  [
    "StandardProductDetailsContainer",
    {
      name: "StandardProductDetailsContainer",
      component: dynamic(() =>
        import("@/components/StandardProductDetailsContainer/StandardProductDetailsContainer").then(
          (m) => m.StandardProductDetailsContainer
        )
      ),
    },
  ],

  // category
  [
    "Category filters",
    {
      name: "Category filters",
      component: dynamic(() => import("@/components/CategoryFilters/CategoryFilters").then((m) => m.CategoryFilters)),
    },
  ],
  [
    "Attribute Filters Drawer",
    {
      name: "Attribute Filters Drawer",
      component: dynamic(() =>
        import("@/components/ProductAttributeFiltersDrawer/ProductAttributeFiltersDrawer").then(
          (m) => m.ProductAttributeFiltersDrawer
        )
      ),
    },
  ],
  [
    "Category items",
    {
      name: "Category items",
      component: dynamic(() => import("@/components/CategoryItems/CategoryItems").then((m) => m.CategoryItems)),
    },
  ],
  [
    "Category name",
    {
      name: "Category name",
      component: dynamic(() => import("@/components/CategoryName/CategoryName").then((m) => m.CategoryName)),
    },
  ],

  // common
  [
    "Accordion",
    {
      name: "Accordion",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/Accordion/Accordion").then((m) => m.Accordion)),
    },
  ],
  [
    "AccordionItem",
    {
      name: "AccordionItem",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/AccordionItem/AccordionItem").then((m) => m.AccordionItem)),
    },
  ],
  [
    "AccountDashboardBanner",
    {
      name: "AccountDashboardBanner",
      component: dynamic(() =>
        import("@/components/AccountDashboardBanner/AccountDashboardBanner").then((m) => m.AccountDashboardBanner)
      ),
    },
  ],
  [
    "AdditionalBenefits",
    {
      name: "AdditionalBenefits",
      component: dynamic(() =>
        import("@/components/AdditionalBenefits/AdditionalBenefits").then((m) => m.AdditionalBenefits)
      ),
    },
  ],
  [
    "AnchorLink",
    {
      name: "AnchorLink",
      component: dynamic(() => import("@/components/AnchorLink/AnchorLink").then((m) => m.AnchorLink)),
    },
  ],
  [
    "AnchorPushComponent",
    {
      name: "AnchorPushComponent",
      component: dynamic(() => import("@/components/AnchorPush/AnchorPush").then((m) => m.AnchorPush)),
    },
  ],
  [
    "ArticleRestrictedContent",
    {
      name: "ArticleRestrictedContent",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      component: dynamic(() =>
        import("@/components/ArticleRestrictedContent/ArticleRestrictedContent").then((m) => m.ArticleRestrictedContent)
      ),
    },
  ],
  [
    "Blog",
    {
      name: "Blog",
      component: dynamic(() => import("@/components/Blog/Blog").then((m) => m.Blog)),
    },
  ],
  [
    "Blog Article",
    {
      name: "Blog Article",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      component: dynamic(() => import("@/components/BlogArticle/BlogArticle").then((m) => m.BlogArticle)),
    },
  ],
  [
    "BlogArticleCarousel",
    {
      name: "BlogArticleCarousel",
      component: dynamic(() =>
        import("@/components/BlogArticleCarousel/BlogArticleCarousel").then((m) => m.BlogArticleCarousel)
      ),
    },
  ],
  [
    "Image",
    {
      name: "Image",
      static: true,
      canHaveChildren: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      component: dynamic(() => import("@/components/BuilderImage/BuilderImage").then((m) => m.BuilderImage)),
    },
  ],
  [
    "CallToAction",
    {
      name: "CallToAction",
      component: dynamic(() => import("@/components/CallToAction/CallToAction").then((m) => m.CallToAction)),
    },
  ],
  [
    "Cart",
    {
      name: "Cart",
      component: dynamic(() => import("@/components/Cart/Cart").then((m) => m.Cart), {
        ssr: true,
      }),
    },
  ],
  [
    "Checkout",
    {
      name: "Checkout",
      component: dynamic(() => import("@/components/Checkout/Checkout").then((m) => m.Checkout), {
        ssr: false,
      }),
    },
  ],
  [
    "Column",
    {
      name: "Column",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/Column/Column").then((m) => m.Column)),
    },
  ],
  [
    "ComingSoon",
    {
      name: "ComingSoon",
      component: dynamic(() => import("@/components/ComingSoon/ComingSoon").then((m) => m.ComingSoon)),
    },
  ],
  [
    "ContactBlock",
    {
      name: "ContactBlock",
      component: dynamic(() => import("@/components/ContactBlock/ContactBlock").then((m) => m.ContactBlock)),
    },
  ],
  [
    "ContactForm",
    {
      name: "ContactForm",
      noWrap: true,
      canHaveChildren: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      component: dynamic(() => import("@/components/ContactForm/ContactForm").then((m) => m.ContactForm)),
    },
  ],
  [
    "Container",
    {
      name: "Container",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/Container/Container").then((m) => m.Container)),
    },
  ],
  [
    "Content Gate",
    {
      name: "Content Gate",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/ContentGate/ContentGate").then((m) => m.ContentGate), {
        ssr: false,
      }),
    },
  ],
  [
    "Consent Reminder Dialog",
    {
      name: "Consent Reminder Dialog",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      component: dynamic(() =>
        import("@/components/ConsentReminderDialog/ConsentReminderDialog").then((m) => m.ConsentReminderDialog)
      ),
    },
  ],
  [
    "Counter",
    {
      name: "Counter",
      component: dynamic(() => import("@/components/Counter/Counter").then((m) => m.Counter)),
    },
  ],
  [
    "CountrySelector",
    {
      name: "CountrySelector",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      component: dynamic(() => import("@/components/CountrySelector/CountrySelector").then((m) => m.CountrySelector)),
    },
  ],
  [
    "CTA",
    {
      name: "CTA",
      component: dynamic(() => import("@/components/Cta/Cta").then((m) => m.Cta)),
    },
  ],
  [
    "CTA Block",
    {
      name: "CTA Block",
      component: dynamic(() => import("@/components/CtaBlock/CtaBlock").then((m) => m.CtaBlock)),
    },
  ],
  [
    "Discount",
    {
      name: "Discount",
      component: dynamic(() => import("@/components/Discount/Discount").then((m) => m.Discount)),
    },
  ],
  [
    "DiscreteHeaderBanner",
    {
      name: "DiscreteHeaderBanner",
      component: dynamic(() =>
        import("@/components/DiscreteHeaderBanner/DiscreteHeaderBanner").then((m) => m.DiscreteHeaderBanner)
      ),
    },
  ],
  [
    "DoubleOptInRedirect",
    {
      name: "DoubleOptInRedirect",
      component: dynamic(() =>
        import("@/components/DoubleOptInRedirect/DoubleOptInRedirect").then((m) => m.DoubleOptInRedirect)
      ),
    },
  ],
  [
    "DownloadButton",
    {
      name: "DownloadButton",
      component: dynamic(() => import("@/components/DownloadButton/DownloadButton").then((m) => m.DownloadButton)),
    },
  ],
  [
    "EarnPoints",
    {
      name: "EarnPoints",
      component: dynamic(() => import("@/components/EarnPoints/EarnPoints").then((m) => m.EarnPoints)),
    },
  ],
  [
    "EcommerceReassuranceBanner",
    {
      name: "EcommerceReassuranceBanner",
      component: dynamic(() =>
        import("@/components/EcommerceReassuranceBanner/EcommerceReassuranceBanner").then(
          (m) => m.EcommerceReassuranceBanner
        )
      ),
    },
  ],
  [
    "EventsLocator",
    {
      name: "EventsLocator",
      component: dynamic(() => import("@/components/EventsLocator/EventsLocator").then((m) => m.EventsLocator), {
        ssr: false,
      }),
    },
  ],
  [
    "Faq",
    {
      name: "Faq",
      component: dynamic(() => import("@/components/Faq/Faq").then((m) => m.Faq)),
    },
  ],
  [
    "FaqArticle",
    {
      name: "FaqArticle",
      component: dynamic(() => import("@/components/FaqArticle/FaqArticle").then((m) => m.FaqArticle)),
    },
  ],
  [
    "FaqResultsContainer",
    {
      name: "FaqResultsContainer",
      component: dynamic(() =>
        import("@/components/FaqResultsContainer/FaqResultsContainer").then((m) => m.FaqResultsContainer)
      ),
    },
  ],
  [
    "FlavorFinder",
    {
      name: "FlavorFinder",
      component: dynamic(() => import("@/components/FlavorFinder/FlavorFinder").then((m) => m.FlavorFinder)),
    },
  ],
  [
    "FlavourCarousel",
    {
      name: "FlavourCarousel",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      component: dynamic(() => import("@/components/FlavourCarousel/FlavourCarousel").then((m) => m.FlavourCarousel)),
    },
  ],
  [
    "FlavourCarouselCard",
    {
      name: "FlavourCarouselCard",
      component: dynamic(() =>
        import("@/components/FlavourCarouselCard/FlavourCarouselCard").then((m) => m.FlavourCarouselCard)
      ),
    },
  ],
  [
    "FooterList",
    {
      name: "FooterList",
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/Footer/components/FooterList/FooterList").then((m) => m.FooterList)
      ),
    },
  ],
  [
    "FormCheckbox",
    {
      name: "FormCheckbox",
      component: dynamic(() => import("@/components/FormCheckbox/FormCheckbox").then((m) => m.FormCheckbox)),
    },
  ],
  [
    "FormConsentCheckboxList",
    {
      name: "FormConsentCheckboxList",
      noWrap: true,
      component: dynamic(() =>
        import("@/components/FormConsentCheckboxList/FormConsentCheckboxList").then((m) => m.FormConsentCheckboxList)
      ),
    },
  ],
  [
    "FormSelect",
    {
      name: "FormSelect",
      component: dynamic(() => import("@/components/FormSelect/FormSelect").then((m) => m.FormSelect)),
    },
  ],
  [
    "FormTextArea",
    {
      name: "FormTextArea",
      component: dynamic(() => import("@/components/FormTextArea/FormTextArea").then((m) => m.FormTextArea)),
    },
  ],
  [
    "FormTextInput",
    {
      name: "FormTextInput",
      component: dynamic(() => import("@/components/FormTextInput/FormTextInput").then((m) => m.FormTextInput)),
    },
  ],
  [
    "GenericLayoutTitle",
    {
      name: "GenericLayoutTitle",
      component: dynamic(() =>
        import("@/components/GenericLayoutTitle/GenericLayoutTitle").then((m) => m.GenericLayoutTitle)
      ),
    },
  ],
  [
    "Grid",
    {
      name: "Grid",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/Grid/Grid").then((m) => m.Grid)),
    },
  ],
  [
    "HeaderBlock",
    {
      name: "HeaderBlock",
      component: dynamic(() => import("@/components/HeaderBlock/HeaderBlock").then((m) => m.HeaderBlock)),
    },
  ],
  [
    "HealthBanner",
    {
      name: "HealthBanner",
      component: dynamic(() =>
        import("@/components/Footer/components/HealthBanner/HealthBanner").then((m) => m.HealthBanner)
      ),
    },
  ],
  [
    "Hero",
    {
      name: "Hero",
      component: dynamic(() => import("@/components/HeroV2/Hero")),
    },
  ],
  [
    "HeroBannerContainer",
    {
      name: "HeroBannerContainer",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/HeroBannerContainer/HeroBannerContainer").then((m) => m.HeroBannerContainer)
      ),
    },
  ],
  [
    "HeroContainer",
    {
      name: "HeroContainer",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/HeroContainer/HeroContainer").then((m) => m.HeroContainer)),
    },
  ],
  [
    "HeroWithCards",
    {
      name: "HeroWithCards",
      component: dynamic(() => import("@/components/HeroBannerWithCards/HeroBannerWithCards")),
    },
  ],
  [
    "HighlightCard",
    {
      name: "HighlightCard",
      component: dynamic(() => import("@/components/HighlightCard/HighlightCard").then((m) => m.HighlightCard)),
    },
  ],
  [
    "ImageGallery",
    {
      name: "ImageGallery",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/ImageGallery/ImageGallery").then((m) => m.ImageGallery)),
    },
  ],
  [
    "ImageGalleryTopic",
    {
      name: "ImageGalleryTopic",
      component: dynamic(() =>
        import("@/components/ImageGalleryTopic/ImageGalleryTopic").then((m) => m.ImageGalleryTopic)
      ),
    },
  ],
  [
    "ImageInfoBanner",
    {
      name: "ImageInfoBanner",
      component: dynamic(() => import("@/components/ImageInfoBanner/ImageInfoBanner").then((m) => m.ImageInfoBanner)),
    },
  ],
  [
    "ImageLink",
    {
      name: "ImageLink",
      component: dynamic(() => import("@/components/ImageLink/ImageLink").then((m) => m.ImageLink)),
    },
  ],
  [
    "ImageNavLinksList",
    {
      name: "ImageNavLinksList",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      component: dynamic(() =>
        import("@/components/SecondaryNavigation/components/ImageNavLinksList/ImageNavLinksList").then(
          (m) => m.ImageNavLinksList
        )
      ),
    },
  ],
  [
    "InfoBanner",
    {
      name: "InfoBanner",
      component: dynamic(() => import("@/components/InfoBanner/InfoBanner").then((m) => m.InfoBanner)),
    },
  ],
  [
    "InformationAboutProducts",
    {
      name: "InformationAboutProducts",
      component: dynamic(() =>
        import("@/components/InformationAboutProducts/InformationAboutProducts").then((m) => m.InformationAboutProducts)
      ),
    },
  ],
  [
    "InnerContainer",
    {
      name: "InnerContainer",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/SecondaryNavigation/components/InnerContainer/InnerContainer").then(
          (m) => m.InnerContainer
        )
      ),
    },
  ],
  [
    "LegalDisclaimer",
    {
      name: "LegalDisclaimer",
      component: dynamic(() => import("@/components/LegalDisclaimer/LegalDisclaimer").then((m) => m.LegalDisclaimer)),
    },
  ],
  [
    "LinksList",
    {
      name: "LinksList",
      component: dynamic(() => import("@/components/LinksList/LinksList").then((m) => m.LinksList)),
    },
  ],
  [
    "List",
    {
      name: "List",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/SecondaryNavigation/components/List/List").then((m) => m.List)),
    },
  ],
  [
    "Logo",
    {
      name: "Logo",
      component: dynamic(() => import("@/components/Logo/Logo").then((m) => m.Logo)),
    },
  ],
  [
    "LoyaltyContactForm",
    {
      name: "LoyaltyContactForm",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/LoyaltyContactForm/LoyaltyContactForm").then((m) => m.LoyaltyContactForm)
      ),
    },
  ],
  [
    "LoyaltyContactFormCheckboxSection",
    {
      name: "LoyaltyContactFormCheckboxSection",
      component: dynamic(() =>
        import("@/components/LoyaltyContactFormCheckboxSection/LoyaltyContactFormCheckboxSection").then(
          (m) => m.LoyaltyContactFormCheckboxSection
        )
      ),
    },
  ],
  [
    "LoyaltyContactFormTextArea",
    {
      name: "LoyaltyContactFormTextArea",
      noWrap: true,
      component: dynamic(() =>
        import("@/components/LoyaltyContactFormTextArea/LoyaltyContactFormTextArea").then(
          (m) => m.LoyaltyContactFormTextArea
        )
      ),
    },
  ],
  [
    "LoyaltyHero",
    {
      name: "LoyaltyHero",
      component: dynamic(() => import("@/components/LoyaltyHeroV2/LoyaltyHero").then((m) => m.LoyaltyHero)),
    },
  ],
  [
    "LoyaltyHeroBannerContainer",
    {
      name: "LoyaltyHeroBannerContainer",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/LoyaltyHeroBannerContainer/LoyaltyHeroBannerContainer").then((m) => m.HeroBannerContainer)
      ),
    },
  ],
  [
    "LoyaltyPointsStatus",
    {
      name: "LoyaltyPointsStatus",
      component: dynamic(() =>
        import("@/components/LoyaltyPointsStatus/LoyaltyPointsStatus").then((m) => m.LoyaltyPointsStatus)
      ),
    },
  ],
  [
    "LoyaltyPushBlock",
    {
      name: "LoyaltyPushBlock",
      component: dynamic(() =>
        import("@/components/LoyaltyPushBlock/LoyaltyPushBlock").then((m) => m.LoyaltyPushBlock)
      ),
    },
  ],
  [
    "LoyaltyReferralStatsDashboard",
    {
      name: "LoyaltyReferralStatsDashboard",
      component: dynamic(() =>
        import("@/components/LoyaltyReferralStats/LoyaltyReferralStats").then((m) => m.LoyaltyReferralStats)
      ),
    },
  ],
  [
    "LoyaltyTabs",
    {
      name: "LoyaltyTabs",
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/LoyaltyTabs/LoyaltyTabs").then((m) => m.LoyaltyTabs)),
    },
  ],
  [
    "LoyaltyVideoPlayer",
    {
      name: "LoyaltyVideoPlayer",
      component: dynamic(() =>
        import("@/components/LoyaltyVideoPlayer/LoyaltyVideoPlayer").then((m) => m.LoyaltyVideoPlayer)
      ),
    },
  ],
  [
    "LoyaltyVideoPreview",
    {
      name: "LoyaltyVideoPreview",
      component: dynamic(() =>
        import("@/components/LoyaltyVideoPreview/LoyaltyVideoPreview").then((m) => m.LoyaltyVideoPreview)
      ),
    },
  ],
  [
    "LoyaltyWrapper",
    {
      name: "LoyaltyWrapper",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/LoyaltyWrapper/LoyaltyWrapper").then((m) => m.LoyaltyWrapper)),
    },
  ],
  [
    "Loyalty Soft Gate",
    {
      name: "Loyalty Soft Gate",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/LoyaltySoftGate/LoyaltySoftGate").then((m) => m.LoyaltySoftGate), {
        ssr: false,
      }),
    },
  ],
  [
    "MgmAccountDashboard",
    {
      name: "MgmAccountDashboard",
      component: dynamic(() =>
        import("@/components/LoyaltyReferralCodeExtended/LoyaltyReferralCodeExtended").then(
          (m) => m.LoyaltyReferralCodeExtended
        )
      ),
    },
  ],
  [
    "MiscellaneousPageContainer",
    {
      name: "MiscellaneousPageContainer",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/MiscellaneousPageContainer/MiscellaneousPageContainer").then(
          (m) => m.MiscellaneousPageContainer
        )
      ),
    },
  ],
  [
    "NotFound",
    {
      name: "NotFound",
      component: dynamic(() => import("@/components/NotFound/NotFound").then((m) => m.NotFound)),
    },
  ],
  [
    "Onsite Personalization Modal",
    {
      name: "Onsite Personalization Modal",
      component: dynamic(
        () =>
          import("@/components/OnsitePersonalizationModal/OnsitePersonalizationModal").then(
            (m) => m.OnsitePersonalizationModal
          ),
        { ssr: false }
      ),
    },
  ],
  [
    "Overview",
    {
      name: "Overview",
      component: dynamic(() => import("@/components/Overview/Overview").then((m) => m.Overview)),
    },
  ],
  [
    "PackCodes",
    {
      name: "PackCodes",
      component: dynamic(() => import("@/components/PackCodes/PackCodes").then((m) => m.PackCodes)),
    },
  ],
  [
    "Personalization Container",
    {
      name: "Personalization Container",
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/PersonalizationContainer/PersonalizationContainer").then((m) => m.PersonalizationContainer)
      ),
    },
  ],
  [
    "PointsAmountBanner",
    {
      name: "PointsAmountBanner",
      component: dynamic(() =>
        import("@/components/PointsAmountBanner/PointsAmountBanner").then((m) => m.PointsAmountBanner)
      ),
    },
  ],
  [
    "PointsHistory",
    {
      name: "PointsHistory",
      component: dynamic(() => import("@/components/PointsHistory/PointsHistory").then((m) => m.PointsHistory)),
    },
  ],
  [
    "PopupVideo",
    {
      name: "PopupVideo",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      component: dynamic(() => import("@/components/PopupVideo/PopupVideo").then((m) => m.PopupVideo)),
    },
  ],
  [
    "Prize",
    {
      name: "Prize",
      component: dynamic(() => import("@/components/Prize/Prize").then((m) => m.Prize)),
    },
  ],
  [
    "ProductCard",
    {
      name: "ProductCard",
      noWrap: true,
      component: dynamic(() => import("@/components/ProductCard/ProductCardWrapper").then((m) => m.ProductCardWrapper)),
    },
  ],
  [
    "ProductCardContainer",
    {
      name: "ProductCardContainer",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/ProductCardContainer/ProductCardContainer").then((m) => m.ProductCardContainer)
      ),
    },
  ],
  [
    "PromoCardHorizontal",
    {
      name: "PromoCardHorizontal",
      component: dynamic(() =>
        import("@/components/PromoCardHorizontal/PromoCardHorizontal").then((m) => m.PromoCardHorizontal)
      ),
    },
  ],
  [
    "PromoCardVertical",
    {
      name: "PromoCardVertical",
      component: dynamic(() =>
        import("@/components/PromoCardVertical/PromoCardVertical").then((m) => m.PromoCardVertical)
      ),
    },
  ],
  [
    "Promotion",
    {
      name: "Promotion",
      component: dynamic(() => import("@/components/Promotion/Promotion").then((m) => m.Promotion)),
    },
  ],
  [
    "Province Popup",
    {
      name: "Province Popup",
      component: dynamic(() => import("@/components/ProvincePopup/ProvincePopup").then((m) => m.ProvincePopup), {
        ssr: false,
      }),
    },
  ],
  [
    "PushBlock",
    {
      name: "PushBlock",
      component: dynamic(() => import("@/components/PushBlock/PushBlock").then((m) => m.PushBlock)),
    },
  ],
  [
    "PushBlockContainer",
    {
      name: "PushBlockContainer",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/PushBlockContainer/PushBlockContainer").then((m) => m.PushBlockContainer)
      ),
    },
  ],
  [
    "QrAgeVerificationContainer",
    {
      name: "QrAgeVerificationContainer",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/QrAgeVerificationContainer/QrAgeVerificationContainer").then(
          (m) => m.QrAgeVerificationContainer
        )
      ),
    },
  ],
  [
    "QrCodeLottery",
    {
      name: "QrCodeLottery",
      component: dynamic(() => import("@/components/QrCodeLottery/QrCodeLottery").then((m) => m.QrCodeLottery)),
    },
  ],
  [
    "QrCodePointsCollection",
    {
      name: "QrCodePointsCollection",
      component: dynamic(() =>
        import("@/components/QrCodePointsCollection/QrCodePointsCollection").then((m) => m.QrCodePointsCollection)
      ),
    },
  ],
  [
    "QuickLinks",
    {
      name: "QuickLinks",
      component: dynamic(() => import("@/components/QuickLinks/QuickLinks").then((m) => m.QuickLinks)),
    },
  ],
  [
    "ReassuranceBanner",
    {
      name: "ReassuranceBanner",
      component: dynamic(() =>
        import("@/components/ReassuranceBanner/ReassuranceBanner").then((m) => m.ReassuranceBanner)
      ),
    },
  ],
  [
    "ReferAFriend",
    {
      name: "ReferAFriend",
      component: dynamic(() => import("@/components/ReferAFriend/ReferAFriend").then((m) => m.ReferAFriend)),
    },
  ],
  [
    "ReferralHistory",
    {
      name: "ReferralHistory",
      component: dynamic(() => import("@/components/ReferralHistory/ReferralHistory").then((m) => m.ReferralHistory)),
    },
  ],
  [
    "ReferralPersonalCode",
    {
      name: "ReferralPersonalCode",
      component: dynamic(() =>
        import("@/components/ReferralPersonalCode/ReferralPersonalCode").then((m) => m.ReferralPersonalCode)
      ),
    },
  ],
  [
    "ReferralStats",
    {
      name: "ReferralStats",
      component: dynamic(() => import("@/components/ReferralStats/ReferralStats").then((m) => m.ReferralStats)),
    },
  ],
  [
    "ResponsiveImage",
    {
      name: "ResponsiveImage",
      component: dynamic(() => import("@/components/ResponsiveImage/ResponsiveImage").then((m) => m.ResponsiveImage)),
    },
  ],
  [
    "RichText",
    {
      name: "RichText",
      component: dynamic(() => import("@/components/RichText/RichText").then((m) => m.RichText)),
    },
  ],
  [
    "Row",
    {
      name: "Row",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() => import("@/components/Row/Row").then((m) => m.Row)),
    },
  ],
  [
    "ScrollDepthTrigger",
    {
      name: "ScrollDepthTrigger",
      component: dynamic(() =>
        import("@/components/ScrollDepthTrigger/ScrollDepthTrigger").then((m) => m.ScrollDepthTrigger)
      ),
    },
  ],
  [
    "Search Form",
    {
      name: "Search Form",
      component: dynamic(() => import("@/components/SearchBox/SearchBox").then((m) => m.SearchBox)),
    },
  ],
  [
    "Search Results",
    {
      name: "Search Results",
      component: dynamic(() => import("@/components/SearchResults/SearchResults").then((m) => m.SearchResults)),
    },
  ],
  [
    "SecondaryNavigation",
    {
      name: "SecondaryNavigation",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(() =>
        import("@/components/SecondaryNavigation/SecondaryNavigation").then((m) => m.SecondaryNavigation)
      ),
    },
  ],
  [
    "Core:Section",
    {
      name: "Core:Section",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/Section/Section").then((m) => m.Section)),
    },
  ],
  [
    "ShopLandingPage",
    {
      name: "ShopLandingPage",
      component: dynamic(() => import("@/components/ShopLandingPage/ShopLandingPage").then((m) => m.ShopLandingPage)),
    },
  ],
  [
    "ShopTeaser",
    {
      name: "ShopTeaser",
      noWrap: true,
      component: dynamic(() =>
        import("@/components/SecondaryNavigation/components/ShopTeaser/ShopTeaser").then((m) => m.ShopTeaser)
      ),
    },
  ],
  [
    "SimpleProductTeaser",
    {
      name: "SimpleProductTeaser",
      component: dynamic(() =>
        import("@/components/SecondaryNavigation/components/SimpleProductTeaser/SimpleProductTeaser").then(
          (m) => m.SimpleProductTeaser
        )
      ),
    },
  ],
  [
    "SimplifiedPrizes",
    {
      name: "SimplifiedPrizes",
      component: dynamic(() =>
        import("@/components/SimplifiedPrizes/SimplifiedPrizes").then((m) => m.SimplifiedPrizes)
      ),
    },
  ],
  [
    "SimplifiedPrizesV2",
    {
      name: "SimplifiedPrizesV2",
      component: dynamic(() =>
        import("@/components/SimplifiedPrizesV2/SimplifiedPrizesV2").then((m) => m.SimplifiedPrizesV2)
      ),
    },
  ],
  [
    "SmartSurveyPushBlock",
    {
      name: "SmartSurveyPushBlock",
      component: dynamic(() =>
        import("@/components/SmartSurveyPushBlock/SmartSurveyPushBlock").then((m) => m.SmartSurveyPushBlock)
      ),
    },
  ],
  [
    "SobQuiz",
    {
      name: "SobQuiz",
      component: dynamic(() => import("@/components/SobQuiz/SobQuiz").then((m) => m.SobQuiz)),
    },
  ],
  [
    "SocialSharingIcons",
    {
      name: "SocialSharingIcons",
      component: dynamic(() =>
        import("@/components/SocialSharingIcons/SocialSharingIcons").then((m) => m.SocialSharingIcons)
      ),
    },
  ],
  [
    "SoftAgeVerification",
    {
      name: "SoftAgeVerification",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      canHaveChildren: true,
      component: dynamic(
        () => import("@/components/SoftAgeVerification/SoftAgeVerification").then((m) => m.SoftAgeVerification),
        { ssr: false }
      ),
    },
  ],
  [
    "StickyContainer",
    {
      name: "StickyContainer",
      canHaveChildren: true,
      component: dynamic(() => import("@/components/StickyContainer/StickyContainer").then((m) => m.StickyContainer), {
        ssr: false,
      }),
    },
  ],
  [
    "StoreDetails",
    {
      name: "StoreDetails",
      component: dynamic(() => import("@/components/StoreDetails/StoreDetails").then((m) => m.StoreDetails)),
    },
  ],
  [
    "HoReCaLocator",
    {
      name: "HoReCaLocator",
      component: dynamic(() => import("@/components/HoReCaLocator/HoReCaLocator").then((m) => m.HoReCaLocator), {
        ssr: false,
      }),
    },
  ],
  [
    "StoreLocator",
    {
      name: "StoreLocator",
      component: dynamic(() => import("@/components/StoreLocator/StoreLocator").then((m) => m.StoreLocator), {
        ssr: false,
      }),
    },
  ],
  [
    "SubmenuTitle",
    {
      name: "SubmenuTitle",
      component: dynamic(() =>
        import("@/components/SecondaryNavigation/components/SubmenuTitle/SubmenuTitle").then((m) => m.SubmenuTitle)
      ),
    },
  ],
  [
    "Survey",
    {
      name: "Survey",
      component: dynamic(() => import("@/components/Survey/Survey").then((m) => m.Survey)),
    },
  ],
  [
    "SurveyMonkey",
    {
      name: "SurveyMonkey",
      component: dynamic(() => import("@/components/SurveyMonkey/SurveyMonkey").then((m) => m.SurveyMonkey)),
    },
  ],
  [
    "Table",
    {
      name: "Table",
      component: dynamic(() => import("@/components/RichText/RichText").then((m) => m.RichText)),
    },
  ],
  [
    "Telephone",
    {
      name: "Telephone",
      component: dynamic(() => import("@/components/Telephone/Telephone").then((m) => m.Telephone)),
    },
  ],
  [
    "TiersBenefits",
    {
      name: "TiersBenefits",
      component: dynamic(() => import("@/components/TiersBenefits/TiersBenefits").then((m) => m.TiersBenefits)),
    },
  ],
  [
    "TileComponent",
    {
      name: "TileComponent",
      component: dynamic(() => import("@/components/Tile/Tile").then((m) => m.Tile)),
    },
  ],
  [
    "TrialHeroBanner",
    {
      name: "TrialHeroBanner",
      component: dynamic(() => import("@/components/TrialHeroBanner/TrialHeroBanner")),
    },
  ],
  [
    "TrustedPlaces",
    {
      name: "TrustedPlaces",
      component: dynamic(() => import("@/components/TrustedPlaces/TrustedPlaces").then((m) => m.TrustedPlaces)),
    },
  ],
  [
    "TrustedPlacesV2",
    {
      name: "TrustedPlacesV2",
      component: dynamic(() => import("@/components/TrustedPlacesV2/TrustedPlacesV2").then((m) => m.TrustedPlacesV2)),
    },
  ],
  [
    "TrustPilotMicroCombo",
    {
      name: "TrustPilotMicroCombo",
      component: dynamic(() =>
        import("@/components/TrustPilotMicroCombo/TrustPilotMicroCombo").then((m) => m.TrustPilotMicroCombo)
      ),
    },
  ],
  [
    "TrustPilotReviewsWidget",
    {
      name: "TrustPilotReviewsWidget",
      component: dynamic(() =>
        import("@/components/TrustPilotReviewsWidget/TrustPilotReviewsWidget").then((m) => m.TrustPilotReviewsWidget)
      ),
    },
  ],
  [
    "UnsubscribeBlock",
    {
      name: "UnsubscribeBlock",
      component: dynamic(() =>
        import("@/components/UnsubscribeBlock/UnsubscribeBlock").then((m) => m.UnsubscribeBlock)
      ),
    },
  ],
  [
    "UserAuth",
    {
      name: "UserAuth",
      component: dynamic(() => import("@/components/UserAuth/UserAuth").then((m) => m.UserAuth), {
        ssr: false,
      }),
    },
  ],
  [
    "UserProfile",
    {
      name: "UserProfile",
      component: dynamic(() => import("@/components/UserProfile/UserProfile").then((m) => m.UserProfile), {
        ssr: false,
      }),
    },
  ],
  [
    "VideoContainerComponent",
    {
      name: "VideoContainerComponent",
      shouldReceiveBuilderProps: {
        builderBlock: true,
      },
      component: dynamic(() => import("@/components/VideoContainer/VideoContainer").then((m) => m.VideoContainer)),
    },
  ],
  [
    "VideoPlayer",
    {
      name: "VideoPlayer",
      component: dynamic(() => import("@/components/VideoPlayer/VideoPlayer").then((m) => m.VideoPlayer)),
    },
  ],
  [
    "Welcome Popup",
    {
      name: "Welcome Popup",
      noWrap: true,
      shouldReceiveBuilderProps: {
        builderBlock: true,
        builderComponents: true,
        builderContext: true,
      },
      component: dynamic(() => import("@/components/WelcomePopup/WelcomePopup").then((m) => m.WelcomePopup), {
        ssr: false,
      }),
    },
  ],
  [
    "X-Selling Popup",
    {
      name: "X-Selling Popup",
      component: dynamic(() => import("@/components/XSellingPopup/XSellingPopup").then((m) => m.XSellingPopup), {
        ssr: false,
      }),
    },
  ],
]);

type BuilderBlockLike = {
  component?: {
    name?: string;
    options?: {
      variants?: Array<{
        blocks?: BuilderBlockLike[];
      }>;
      columns?: { blocks?: BuilderBlockLike[] }[];
      [key: string]: unknown;
      tabList?: { blocks?: BuilderBlockLike[] }[];
    };
  };
  children?: BuilderBlockLike[];
};

type BuilderComponentOptions = NonNullable<NonNullable<BuilderBlockLike["component"]>["options"]>;

type BuilderContentLike = {
  data?: {
    blocks?: BuilderBlockLike[];
  };
  variations?: Record<
    string,
    {
      data?: {
        blocks?: BuilderBlockLike[];
      };
    }
  >;
} | null;

function isBlockArray(value: unknown): value is BuilderBlockLike[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return typeof first === "object" && first !== null && ("component" in first || "children" in first);
}

function getComponentNamesFromContent(content: BuilderContentLike | undefined): Set<string> {
  const names = new Set<string>();
  const blocks = content?.data?.blocks;

  if (Array.isArray(blocks)) {
    collectComponentNamesFromBlocks(blocks, names);
  }

  // The SDK selects the A/B variation, so register components from every possible variation.
  for (const variation of Object.values(content?.variations ?? {})) {
    const variationBlocks = variation?.data?.blocks;
    if (Array.isArray(variationBlocks)) {
      collectComponentNamesFromBlocks(variationBlocks, names);
    }
  }

  return names;
}

function collectComponentNamesFromVariants(
  variants: NonNullable<BuilderComponentOptions["variants"]>,
  names: Set<string>
): void {
  for (const variant of variants) {
    if (!variant || typeof variant !== "object") continue;
    const variantBlocks = variant.blocks;
    if (Array.isArray(variantBlocks)) {
      collectComponentNamesFromBlocks(variantBlocks, names);
    }
  }
}

function collectComponentNamesFromBlocks(blocks: BuilderBlockLike[], names: Set<string>): void {
  for (const block of blocks) {
    if (block.component?.name) {
      names.add(block.component.name);
    }

    const options = block.component?.options;
    if (options && Array.isArray(options.variants)) {
      collectComponentNamesFromVariants(options.variants, names);
    }

    if (Array.isArray(block.children)) {
      collectComponentNamesFromBlocks(block.children, names);
    }

    const columns = block.component?.options?.columns ?? [];

    for (const column of columns) {
      if (Array.isArray(column.blocks)) {
        collectComponentNamesFromBlocks(column.blocks, names);
      }
    }

    if (options) {
      for (const key of Object.keys(options)) {
        if (key === "variants" || key === "columns") continue;
        const value = options[key];
        if (isBlockArray(value)) {
          collectComponentNamesFromBlocks(value, names);
        }
      }
    }

    const tabList = block.component?.options?.tabList ?? [];

    for (const tab of tabList) {
      if (Array.isArray(tab.blocks)) {
        collectComponentNamesFromBlocks(tab.blocks, names);
      }
    }
  }
}

export function filterComponentsByContent(content: BuilderContentLike | undefined): RegisteredComponent[] {
  const usedNames = getComponentNamesFromContent(content);

  const imageLinkComponent = registry.get("ImageLink");

  if (usedNames.size === 0) {
    return imageLinkComponent ? [imageLinkComponent] : [];
  }

  const components: RegisteredComponent[] = [];

  if (imageLinkComponent) {
    components.push(imageLinkComponent);
  }

  for (const name of usedNames) {
    if (name === "ImageLink") continue;
    const component = registry.get(name);
    if (component) {
      components.push(component);
    }
  }

  return components;
}
